/**
 * Which screen a route shows, and the header's title and line for it, in the brand's words.
 */

import { Analytics } from "@/features/analytics";
import { BrandSettings } from "@/features/brand";
import { History } from "@/features/history";
import { Home } from "@/features/home";
import { ItemEditor, itemCrumbs } from "@/features/item-editor";
import { ItemList } from "@/features/library";
import { MediaLibrary } from "@/features/media";
import { Pricing } from "@/features/pricing";
import { ReviewPublish } from "@/features/review";
import { Advanced, Team } from "@/features/settings";
import { ScreensEditor } from "@/features/your-app";
import { PageBar } from "@/components/page-bar";
import type { Route } from "@/lib/route";
import { lower, noun, TERMS } from "@/vocabulary/vocabulary";
import { useStudio, type Studio } from "./studio";

export function screenMeta(route: Route, studio: Studio): { title: string; subtitle?: string } {
  const { pack, client } = studio;
  const type = route.type ?? "title";
  const items = lower(noun(pack, "title", "many"));
  if (route.id && route.show !== "new" && (route.section === "library" || route.section === "live")) {
    return { title: studio.labelOf(type, route.id) ?? noun(pack, type) };
  }
  switch (route.section) {
    case "home": return { title: "Home", subtitle: `What needs you, what is waiting to go live, and how ${client.brand.name} is doing.` };
    case "library": return { title: noun(pack, type, "many"), subtitle: type === "title" ? `Your ${items}: add them, edit them, and choose which show in the app.` : `Part of your Library. Changes reach ${TERMS.feed} when you publish.` };
    case "live": return { title: "Live & schedule", subtitle: `${noun(pack, "liveEvent", "many")} with their start times, as a list or a schedule.` };
    case "app": return { title: "Your app", subtitle: `Each screen of your app with its ${TERMS.rails}, and how it looks on a TV or a phone.` };
    case "analytics": return { title: "Analytics", subtitle: `Who watches ${client.brand.name}, what they watch and how much, in plain numbers.` };
    case "pricing": return { title: "Pricing & access", subtitle: "What you sell, and access you give with codes and guest lists." };
    case "review": return { title: "Review & publish", subtitle: "Everything that changes in the apps when you publish. Nothing goes out until you do." };
    case "settings":
      switch (route.page) {
        case "team": return { title: "Team", subtitle: "Who can work on this brand, and what each role may do." };
        case "history": return { title: "History", subtitle: "Every publish, and everything the team did." };
        case "files": return { title: "Files", subtitle: "Every video and image you uploaded, and how much storage is left." };
        case "advanced": return { title: "Advanced", subtitle: "Language, catalogue import and test-build previews. Owners only." };
        default: return { title: "Brand", subtitle: "Your name, colour, languages, features and menu, as the apps show them." };
      }
  }
}

export function Screen({ route }: { route: Route }) {
  const { client, pack, canEdit, isOwner } = useStudio();
  if (route.section === "app") return <ScreensEditor key={route.id ?? ""} route={route} />;
  const isItem = route.section === "library" || route.section === "live";
  if (isItem && route.id) return <ItemEditor key={`${route.type}/${route.id}`} route={route} />;
  if (isItem) {
    return <ItemList key={`${route.section}/${route.type}`} route={route} />;
  }
  switch (route.section) {
    case "home": return <Home route={route} />;
    case "review": return <ReviewPublish />;
    case "analytics": return <Analytics route={route} />;
    case "pricing": return <Pricing />;
    case "settings":
      switch (route.page) {
        case "team": return <Team />;
        case "history": return <History />;
        case "files": return <MediaLibrary client={client} canEdit={canEdit} />;
        case "advanced": return <Advanced />;
        default: return <BrandSettings client={client} isOwner={isOwner} />;
      }
  }
  return <PageBar crumbs={itemCrumbs(route, pack)} />;
}
